import React, { useState, useEffect } from 'react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useEscapeKey } from '../hooks/useEscapeKey';
import '../styles/AutoFollowModal.css';

/**
 * Modal that appears when a premium user follows or friends someone
 * Asks if they want to auto-follow that person's current and/or future lists
 */
function AutoFollowModal({ isOpen, onClose, onConfirm, targetUser, actionType }) {
  const { isPremium } = useSubscription();
  const [autoFollowOption, setAutoFollowOption] = useState('none');

  // Handle escape key
  useEscapeKey(isOpen && isPremium, onClose);

  // For non-premium users, skip the modal and just proceed with the action
  useEffect(() => {
    if (isOpen && !isPremium) {
      onConfirm('none');
      onClose();
    }
  }, [isOpen, isPremium, onConfirm, onClose]);

  if (!isOpen || !isPremium) {
    return null;
  }

  const displayName = targetUser?.displayName || targetUser?.username || 'this user';

  const handleConfirm = () => {
    onConfirm(autoFollowOption);
    onClose();
  };

  const handleSkip = () => {
    onConfirm('none');
    onClose();
  };

  return (
    <div className="auto-follow-modal-overlay" onClick={onClose}>
      <div className="auto-follow-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="auto-follow-modal-header">
          <h3>Auto-Follow Lists</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <div className="auto-follow-modal-body">
          <p className="auto-follow-description">
            Would you like to automatically follow lists from <strong>@{targetUser?.username || displayName}</strong>?
          </p>

          <div className="auto-follow-options">
            <label className="auto-follow-option">
              <input
                type="radio"
                name="autoFollow"
                value="none"
                checked={autoFollowOption === 'none'}
                onChange={(e) => setAutoFollowOption(e.target.value)}
              />
              <div className="option-content">
                <span className="option-title">No thanks</span>
                <span className="option-description">I'll follow lists manually</span>
              </div>
            </label>

            <label className="auto-follow-option">
              <input
                type="radio"
                name="autoFollow"
                value="current"
                checked={autoFollowOption === 'current'}
                onChange={(e) => setAutoFollowOption(e.target.value)}
              />
              <div className="option-content">
                <span className="option-title">Current lists only</span>
                <span className="option-description">Follow all their existing lists now</span>
              </div>
            </label>

            <label className="auto-follow-option">
              <input
                type="radio"
                name="autoFollow"
                value="future"
                checked={autoFollowOption === 'future'}
                onChange={(e) => setAutoFollowOption(e.target.value)}
              />
              <div className="option-content">
                <span className="option-title">Future lists only</span>
                <span className="option-description">Auto-follow new lists they create</span>
              </div>
            </label>

            <label className="auto-follow-option">
              <input
                type="radio"
                name="autoFollow"
                value="all"
                checked={autoFollowOption === 'all'}
                onChange={(e) => setAutoFollowOption(e.target.value)}
              />
              <div className="option-content">
                <span className="option-title">All lists</span>
                <span className="option-description">Follow all current lists + auto-follow new ones</span>
              </div>
            </label>
          </div>
        </div>
        <div className="auto-follow-modal-actions">
          <button className="btn-secondary" onClick={handleSkip}>
            Skip
          </button>
          <button className="btn-primary" onClick={handleConfirm}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

export default AutoFollowModal;
